import React, { useMemo } from 'react'
import { ReactComponent as FTPIcon } from '../../../../../images/icons/ftp.svg'
import { ReactComponent as WarningIcon } from '../../../../../images/icons/warning.svg'
import { Button, LoadingComponent, SelectInput, TextInput } from '@demandlocal/components'
import { FieldValues, FormProvider, useForm } from 'react-hook-form'
import Modal from '../../../../../components/Modal/Modal'
import toast from 'react-hot-toast'
import { useSubmitInventoryDataProviderRequest } from '../../../../../api/dealership/inventory/useSubmitInventoryDataProviderRequest'
import { useNavigate } from 'react-router'

export default function ConnectInventoryDataProvider(): JSX.Element {
  const formContext = useForm({
    mode: 'onSubmit',
  })
  const {
    watch,
    handleSubmit,
    formState: { isSubmitting },
  } = formContext

  const { mutateAsync: submitDataProviderRequest } =
    useSubmitInventoryDataProviderRequest()

  const providerType = watch('inventory_data_provider_name')

  const shouldShowTooltip = useMemo(
    () =>
      (providerType && providerType?.value === 'vAuto') ||
      providerType?.value === 'HomeNet',
    [providerType],
  )

  const navigate = useNavigate()

  const onSubmit = async (values: Partial<FieldValues>): Promise<void> => {
    const toastId = toast.loading('Submitting data provider request')
    try {
      await submitDataProviderRequest({
        data: {
          provider: values.inventory_data_provider_name.value,
          provider_id: values?.provider_id,
        },
      })
      toast.success(
        'Successfully submitted request, you should hear back about in 24 hours.',
        { id: toastId, duration: 5000 },
      )
      navigate(-1)
    } catch (err) {
      toast.error(
        'Unable to submit data provider request at this time, please contact your LinkOne representative if this problem persists.',
        { id: toastId, duration: 5000 },
      )
    }
  }

  return (
    <Modal key={'ConnectInventory'} isOpen modalRoute>
      <FormProvider {...formContext}>
        <div
          className={
            'flex flex-1 h-full flex-col *:px-12 pt-12 relative justify-between'
          }>
          {isSubmitting && <LoadingComponent title={'Loading ...'} />}
          <div className="flex flex-col gap-5">
            <div className={'flex flex-row gap-5 items-center'}>
              <FTPIcon />
              <h1 className={'text-[28px] font-bold'}>Submit a Data Provider Request</h1>
            </div>
            <div>
              <span>
                This can take up to 24 hours for us to verify and setup this integration
                for you.
              </span>
            </div>
          </div>
          <div className={'flex flex-col p-10 gap-5 rounded-lg mt-12'}>
            <SelectInput
              name={'inventory_data_provider_name'}
              label={'Provider'}
              placeholder={'Provider'}
              options={[
                { value: 'vAuto', label: 'vAuto' },
                { value: 'HomeNet', label: 'HomeNet' },
                { value: 'FTP', label: 'SFTP/FTP' },
              ]}
            />
            {shouldShowTooltip && (
              <div className="flex flex-row p-3 gap-3 items-center rounded-md border-2 border-secondary-engineering-red/40 bg-secondary-engineering-red/20">
                <WarningIcon />
                <span className="text-xs font-bold">
                  Make sure to contact your provider, instructing them to send your data
                  to LinkOne, in order for us to connect to your inventory
                </span>
              </div>
            )}
            <TextInput
              name="provider_id"
              label="Provider ID"
              inverted
              placeholder="If you have your provider ID for HomeNet/vAuto, or FTP file name, put this here!"
            />
          </div>
          <div className={'h-20 items-center flex justify-between border-t-2 py-4'}>
            <Button
              variant={'secondary'}
              onClick={(e) => {
                e.preventDefault()
                navigate(-1)
              }}>
              Close
            </Button>
            <Button
              disabled={false}
              variant={'primary-blue'}
              onClick={(e) => {
                e.preventDefault()
                handleSubmit(onSubmit)()
              }}>
              Submit
            </Button>
          </div>
        </div>
      </FormProvider>
    </Modal>
  )
}
